// Catalog of services Lisan offers. Listed in the order they appear in
// filter dropdowns and KPI breakdowns. Surfacing them as a closed list keeps
// the data clean — when the seed/back-office adds a new one, surface it here
// so it picks up a stable color in the UI.

export const CLIENT_SERVICES = [
  "Monthly CFO",
  "Fundraise Readiness",
  "Check-up 360°",
  "HR Structuring",
  "Legal Setup",
  "Admin Outsourcing",
] as const;

export type ClientService = (typeof CLIENT_SERVICES)[number];

export interface ServiceStyle {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

const SERVICE_STYLES: Record<string, ServiceStyle> = {
  "Monthly CFO": {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
    dot: "bg-indigo-500",
  },
  "Fundraise Readiness": {
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-200",
    dot: "bg-violet-500",
  },
  "Check-up 360°": {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  "HR Structuring": {
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    border: "border-cyan-200",
    dot: "bg-cyan-500",
  },
  "Legal Setup": {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  "Admin Outsourcing": {
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-200",
    dot: "bg-orange-500",
  },
};

const FALLBACK_SERVICE_STYLE: ServiceStyle = {
  bg: "bg-slate-50",
  text: "text-slate-700",
  border: "border-slate-200",
  dot: "bg-slate-400",
};

export function getServiceStyle(
  service: string | null | undefined,
): ServiceStyle {
  if (!service) return FALLBACK_SERVICE_STYLE;
  return SERVICE_STYLES[service] ?? FALLBACK_SERVICE_STYLE;
}

const DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Days elapsed since `dateIso`. Returns 0 for null / invalid / future dates.
 */
export function daysSince(dateIso: string | null | undefined): number {
  if (!dateIso) return 0;
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / DAY_MS));
}

/**
 * Coarse-grained relationship duration: "1 day", "3 weeks", "8 months",
 * "2 years". Used as the muted second line of the "Client since" cell.
 */
export function formatRelationshipDuration(
  fromIso: string | null | undefined,
): string | null {
  const days = daysSince(fromIso);
  if (!fromIso || days <= 0) return null;
  if (days < 7) return days === 1 ? "1 day" : `${days} days`;
  if (days < 30) {
    const weeks = Math.max(1, Math.floor(days / 7));
    return weeks === 1 ? "1 week" : `${weeks} weeks`;
  }
  if (days < 365) {
    const months = Math.max(1, Math.floor(days / 30));
    return months === 1 ? "1 month" : `${months} months`;
  }
  const years = Math.max(1, Math.floor(days / 365));
  return years === 1 ? "1 year" : `${years} years`;
}
